import {
  Add,
  AccountBalance,
  Apartment,
  Gavel,
  LocationCity,
  Search,
  Settings,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import {
  useAdministrativeUnits,
  useAuthorities,
  useMunicipalities,
  useAdminUnitMunicipalities,
  useCourtAdministrativeUnitAssignments,
  useCourtRoles,
  useCourts,
  useFederalStates,
  usePostCodes,
} from '../../Aemter/hooks';
import { AddAuthorityDialog } from '../../Aemter/Authorities/AddAuthorityDialog';
import { AddMunicipalityDialog } from '../../Aemter/Municipalities/AddMunicipalityDialog';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import { AddAdminUnitDialog } from '../../Aemter/AdminUnits/AddAdminUnitDialog';
import { AddCourtDialog } from '../../Aemter/Courts/AddCourtDialog';
import { CourtAssignmentDetailPane } from './CourtAssignmentDetailPane';
import { MunicipalityDetailPane } from './MunicipalityDetailPane';
import { CourtRolesManager } from './CourtRolesManager';
import type { IFederalState } from '../../Interfaces/IAemter';
import type { ICourt } from '../../Interfaces/ICourt';

// Wrapper, der `usePostCodes` erst beim Öffnen des Dialogs aktiviert. So
// blockiert das Postcodes-Endpoint (langsam, riesig) nicht den initialen
// Render der Ämter-Workbench. Beim Öffnen erscheint kurz ein Spinner; erst
// wenn die Postleitzahlen-Liste da ist, wird der eigentliche Dialog gerendert.
const AddCourtDialogLazy: React.FC<{
  courts: ICourt[];
  federalStates: IFederalState[];
  onClose: () => void;
}> = ({ courts, federalStates, onClose }) => {
  const { postcodes, isLoadingPostcodes } = usePostCodes();
  if (isLoadingPostcodes) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.2)',
          zIndex: 1300,
        }}
      >
        <LoadingSpinner />
      </Box>
    );
  }
  return (
    <AddCourtDialog
      courts={courts}
      federalStates={federalStates}
      postcodes={postcodes ?? []}
      onClose={onClose}
    />
  );
};

type Mode = 'gemarkung' | 'municipality' | 'court' | 'authority';

/**
 * Zentrale Ämter-Workbench: ein einheitlicher Ort, um Gemarkungen, Gerichte
 * und ihre Rollen-Zuordnungen zu pflegen. Die alte 3-Spalten-Kaskade
 * (Bundesland → Gericht → Gemarkung) ist durch zwei flexible Modi ersetzt:
 *
 *   • „Gemarkungen": Gemarkung wählen → für die Gemarkung alle Gerichte
 *     mit ihren Rollen sehen und bearbeiten (Standard-Workflow).
 *   • „Gerichte": Gericht wählen → sehen, in welchen Gemarkungen es welche
 *     Rolle innehat.
 *
 * Stammdaten-Pflege (Bundesländer/Gerichtsrollen) ist über Toolbar-Buttons
 * erreichbar — dadurch bleiben sie verfügbar, drängen sich aber nicht in den
 * primären Pfad.
 */
export const CourtOverview: React.FC = () => {
  const { courts, isLoadingCourts, refetchCourts } = useCourts();
  const { authorities, isLoadingAuthorities, refetchAuthorities } =
    useAuthorities();
  const { municipalities, isLoadingMunicipalities, refetchMunicipalities } =
    useMunicipalities();
  const {
    adminUnitMunicipalities,
    isLoadingAdminUnitMunicipalities,
    refetchAdminUnitMunicipalities,
  } = useAdminUnitMunicipalities();
  const { courtRoles, isLoadingCourtRoles, refetchCourtRoles } =
    useCourtRoles();
  const { federalStates, isLoadingFederalStates } = useFederalStates();
  const {
    administrativeUnits,
    isLoadingAdministrativeUnits,
    refetchAdministrativeUnits,
  } = useAdministrativeUnits();
  const {
    courtAdministrativeUnitAssignments,
    isLoadingCourtAdministrativeUnitAssignments,
    refetchCourtAdministrativeUnitAssignments,
  } = useCourtAdministrativeUnitAssignments();

  const [mode, setMode] = useState<Mode>('gemarkung');
  const [federalStateFilter, setFederalStateFilter] = useState<number | 'all'>(
    'all'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [showAddCourt, setShowAddCourt] = useState(false);
  const [showAddAuthority, setShowAddAuthority] = useState(false);
  const [showAddMunicipality, setShowAddMunicipality] = useState(false);
  const [showRolesManager, setShowRolesManager] = useState(false);

  // ALLE Hooks unbedingt vor frühen Returns — sonst „Rendered more hooks
  // than during the previous render" beim Übergang Loading → Loaded.
  // Postcodes werden hier NICHT mehr abgewartet: das `/postcode`-Endpoint ist
  // riesig und langsam; der AddCourtDialog braucht sie sowieso erst beim
  // Öffnen. Wir laden sie lazy im Dialog selber.
  const visibleItems = useMemo(() => {
    type Item = {
      id: number;
      primary: string;
      secondary: string;
      count: number;
    };
    if (
      !administrativeUnits ||
      !courts ||
      !authorities ||
      !municipalities ||
      !adminUnitMunicipalities ||
      !federalStates ||
      !courtAdministrativeUnitAssignments
    ) {
      return [] as Item[];
    }
    const fsName = (idFederalState?: number) =>
      federalStates.find(f => f.idFederalState === idFederalState)
        ?.FederalState ?? '';
    const inFilter = (idFederalState?: number) =>
      federalStateFilter === 'all' || idFederalState === federalStateFilter;
    const term = searchTerm.trim().toLowerCase();

    if (mode === 'gemarkung') {
      return administrativeUnits
        .filter(u => inFilter(u.idFederalState))
        .filter(u =>
          term ? u.AdministrativeUnit.toLowerCase().includes(term) : true
        )
        .sort((a, b) =>
          a.AdministrativeUnit.localeCompare(b.AdministrativeUnit)
        )
        .map(u => ({
          id: u.idAdministrativeUnit,
          primary: u.AdministrativeUnit,
          secondary: fsName(u.idFederalState),
          count: courtAdministrativeUnitAssignments.filter(
            a => a.idAdministrativeUnit === u.idAdministrativeUnit
          ).length,
        }));
    }
    if (mode === 'court') {
      return courts
        .filter(c => inFilter(c.idFederalState))
        .filter(c => (term ? c.Court.toLowerCase().includes(term) : true))
        .sort((a, b) => a.Court.localeCompare(b.Court))
        .map(c => ({
          id: c.idCourt,
          primary: c.Court,
          secondary: fsName(c.idFederalState),
          count: courtAdministrativeUnitAssignments.filter(
            a => a.idCourt === c.idCourt
          ).length,
        }));
    }
    if (mode === 'authority') {
      return authorities
        .filter(a => inFilter(a.idFederalState))
        .filter(a => (term ? a.Authority.toLowerCase().includes(term) : true))
        .sort((a, b) => a.Authority.localeCompare(b.Authority))
        .map(a => ({
          id: a.idAuthority,
          primary: a.Authority,
          secondary: fsName(a.idFederalState),
          count: courtAdministrativeUnitAssignments.filter(
            x => x.idAuthority === a.idAuthority
          ).length,
        }));
    }
    if (mode === 'municipality') {
      return municipalities
        .filter(m => inFilter(m.idFederalState))
        .filter(m =>
          term ? m.Municipality.toLowerCase().includes(term) : true
        )
        .sort((a, b) => a.Municipality.localeCompare(b.Municipality))
        .map(m => ({
          id: m.idMunicipality,
          primary: m.Municipality,
          secondary: fsName(m.idFederalState),
          count: adminUnitMunicipalities.filter(
            x =>
              x.idMunicipality === m.idMunicipality &&
              x.activeAssignment !== false
          ).length,
        }));
    }
    return [] as Item[];
  }, [
    mode,
    federalStateFilter,
    searchTerm,
    administrativeUnits,
    courts,
    authorities,
    municipalities,
    adminUnitMunicipalities,
    federalStates,
    courtAdministrativeUnitAssignments,
  ]);

  if (
    isLoadingCourts ||
    isLoadingAuthorities ||
    isLoadingMunicipalities ||
    isLoadingAdminUnitMunicipalities ||
    isLoadingCourtRoles ||
    isLoadingFederalStates ||
    isLoadingAdministrativeUnits ||
    isLoadingCourtAdministrativeUnitAssignments
  ) {
    return <LoadingSpinner height='80vh' />;
  }
  if (
    !courts ||
    !authorities ||
    !municipalities ||
    !adminUnitMunicipalities ||
    !courtRoles ||
    !federalStates ||
    !administrativeUnits ||
    !courtAdministrativeUnitAssignments
  ) {
    return (
      <Alert severity='error'>Die Daten konnten nicht geladen werden!</Alert>
    );
  }

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    setSelectedId(null);
  };

  const onAnyChange = () => {
    refetchCourts();
    refetchAuthorities();
    refetchMunicipalities();
    refetchAdminUnitMunicipalities();
    refetchAdministrativeUnits();
    refetchCourtAdministrativeUnitAssignments();
  };

  return (
    <Box>
      {/* === Toolbar === */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Box>
          <Typography variant='h5'>Ämterverwaltung</Typography>
          <Typography variant='caption' color='text.secondary'>
            Bundesländer und Gerichte für Maßnahmen-Zuordnungen pflegen.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<Settings />}
            onClick={() => setShowRolesManager(true)}
          >
            Gerichtsrollen pflegen
          </Button>
        </Stack>
      </Stack>

      {/* === Filter-Leiste === */}
      <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size='small'
            onChange={(_, v) => v && handleModeChange(v as Mode)}
          >
            <ToggleButton value='gemarkung'>
              <Apartment fontSize='small' sx={{ mr: 0.5 }} />
              Gemarkung
            </ToggleButton>
            <ToggleButton value='municipality'>
              <LocationCity fontSize='small' sx={{ mr: 0.5 }} />
              Politische Gemeinde
            </ToggleButton>
            <ToggleButton value='court'>
              <Gavel fontSize='small' sx={{ mr: 0.5 }} />
              Gerichte
            </ToggleButton>
            <ToggleButton value='authority'>
              <AccountBalance fontSize='small' sx={{ mr: 0.5 }} />
              Zuständigkeiten
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            size='small'
            label='Bundesland'
            value={federalStateFilter}
            onChange={e =>
              setFederalStateFilter(
                e.target.value === 'all' ? 'all' : Number(e.target.value)
              )
            }
            sx={{ minWidth: 200 }}
          >
            <MenuItem value='all'>Alle Bundesländer</MenuItem>
            {federalStates
              .slice()
              .sort((a, b) => a.FederalState.localeCompare(b.FederalState))
              .map(fs => (
                <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                  {fs.FederalState}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            size='small'
            placeholder={
              mode === 'gemarkung'
                ? 'Gemarkung suchen…'
                : mode === 'court'
                  ? 'Gericht suchen…'
                  : mode === 'authority'
                    ? 'Behörde suchen…'
                    : 'Gemeinde suchen…'
            }
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant='contained'
            startIcon={<Add />}
            onClick={() =>
              mode === 'gemarkung'
                ? setShowAddUnit(true)
                : mode === 'court'
                  ? setShowAddCourt(true)
                  : mode === 'authority'
                    ? setShowAddAuthority(true)
                    : setShowAddMunicipality(true)
            }
          >
            {mode === 'gemarkung'
              ? 'Neue Gemarkung'
              : mode === 'court'
                ? 'Neues Gericht'
                : mode === 'authority'
                  ? 'Neue Behörde'
                  : 'Neue Gemeinde'}
          </Button>
        </Stack>
      </Paper>

      {/* === Workbench === */}
      {
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Paper
            variant='outlined'
            sx={{
              width: 320,
              flexShrink: 0,
              maxHeight: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {visibleItems.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  Keine Einträge gefunden.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
                {visibleItems.map(item => (
                  <ListItem key={item.id} disablePadding divider>
                    <ListItemButton
                      selected={selectedId === item.id}
                      onClick={() => setSelectedId(item.id)}
                    >
                      <ListItemText
                        primary={item.primary}
                        secondary={item.secondary}
                      />
                      <Chip
                        label={item.count}
                        size='small'
                        color={item.count > 0 ? 'primary' : 'default'}
                        variant={item.count > 0 ? 'filled' : 'outlined'}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {selectedId !== null ? (
              mode === 'municipality' ? (
                municipalities.find(m => m.idMunicipality === selectedId) ? (
                  <MunicipalityDetailPane
                    municipality={
                      municipalities.find(m => m.idMunicipality === selectedId)!
                    }
                    federalStates={federalStates}
                    administrativeUnits={administrativeUnits}
                    mappings={adminUnitMunicipalities}
                    assignments={courtAdministrativeUnitAssignments}
                    courts={courts}
                    authorities={authorities}
                    courtRoles={courtRoles}
                    onAnyChange={onAnyChange}
                  />
                ) : null
              ) : (
                <CourtAssignmentDetailPane
                  mode={mode}
                  entityId={selectedId}
                  federalStates={federalStates}
                  courts={courts}
                  authorities={authorities}
                  administrativeUnits={administrativeUnits}
                  courtRoles={courtRoles}
                  assignments={courtAdministrativeUnitAssignments}
                  onAnyChange={onAnyChange}
                />
              )
            ) : (
              <Paper variant='outlined' sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                  {mode === 'gemarkung'
                    ? 'Wählen Sie eine Gemarkung aus der Liste, um die zuständigen Stellen zu pflegen.'
                    : mode === 'court'
                      ? 'Wählen Sie ein Gericht aus der Liste, um seine Gemarkungs-Zuordnungen zu pflegen.'
                      : mode === 'authority'
                        ? 'Wählen Sie eine Behörde aus der Liste, um ihre Gemarkungs-Zuordnungen zu pflegen.'
                        : 'Wählen Sie eine Gemeinde aus der Liste, um Gemarkungen zuzuordnen und die Zuständigkeiten zu sehen.'}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      }

      {/* === Dialogs === */}
      {showAddUnit && (
        <AddAdminUnitDialog
          onClose={() => setShowAddUnit(false)}
          currentAdminUnits={administrativeUnits}
          federalStates={federalStates}
        />
      )}
      {showAddCourt && (
        <AddCourtDialogLazy
          courts={courts}
          federalStates={federalStates}
          onClose={() => setShowAddCourt(false)}
        />
      )}
      {showAddAuthority && (
        <AddAuthorityDialog
          authorities={authorities}
          federalStates={federalStates}
          onClose={() => setShowAddAuthority(false)}
          onCreated={onAnyChange}
        />
      )}
      {showAddMunicipality && (
        <AddMunicipalityDialog
          municipalities={municipalities}
          federalStates={federalStates}
          onClose={() => setShowAddMunicipality(false)}
          onCreated={onAnyChange}
        />
      )}
      {showRolesManager && (
        <CourtRolesManager
          courtRoles={courtRoles}
          onClose={() => setShowRolesManager(false)}
          onChange={() => refetchCourtRoles()}
        />
      )}
    </Box>
  );
};
